import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import styled from 'styled-components';

import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import OutlineButton from 'packages/kakapo-ui-kit/components/buttons/OutlineButton';
import { ButtonIndication, ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import { IWithId } from 'packages/kakapo-ui-services/services/common/ListService';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';

export const ActionListItemTitle = ({ title }: { title: string }) => {
	const { t } = useTranslation();

	return (
		<>
			<Box mb={2}>
				<SubSectionTitle>{t('common.options')}</SubSectionTitle>
			</Box>

			<Box mb={4}>
				<HorizontalRule />
			</Box>
		</>
	);
};

const DisabledText = styled.span`
	color: ${(props) => props.theme.colors.antracite};
	font-weight: 700;
`;

const ActionListItem = ({
	label,
	buttonTitle,
	buttonIndication,
	buttonDisabled,
	onButtonClick,
	disabledText,
}: {
	label: string;
	buttonTitle: string;
	buttonIndication?: ButtonIndication;
	buttonDisabled?: boolean;
	onButtonClick: (event: React.MouseEvent) => void;
	disabledText?: string;
}) => {
	return (
		<Flex mb={3} alignItems="center">
			<Box mr={2} width={5 / 12} flexGrow={1}>
				<Label title={label} spaced={false} />
				{buttonDisabled && disabledText && <DisabledText>{disabledText}</DisabledText>}
			</Box>
			<Box>
				<OutlineButton
					indication={buttonIndication}
					size={ButtonSize.Small}
					onClick={onButtonClick}
					disabled={buttonDisabled}
				>
					{buttonTitle}
				</OutlineButton>
			</Box>
		</Flex>
	);
};

interface IArchiveable {
	isArchived: boolean;
}

interface IArchiveableInput {
	variables: {
		input: {
			id: string;
		};
	};
}

export const ActionArchiveListItem = <T extends IWithId & IArchiveable>({
	archivableItem,
	archivableItemName,
	archivableItemKey,
	useArchive,
	useRestore,
	buttonDisabled,
}: {
	archivableItem: T;
	archivableItemName: string;
	archivableItemKey: string;
	useArchive: () => [(input: IArchiveableInput) => void, {}];
	useRestore: () => [(input: IArchiveableInput) => void, {}];
	buttonDisabled?: boolean;
}) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [archive] = useArchive();
	const [restore] = useRestore();

	const { t } = useTranslation();

	const toggleArchived = async () => {
		setIsLoading(true);

		if (archivableItem.isArchived) {
			await restore({
				variables: {
					input: {
						id: archivableItem.id,
					},
				},
			});
		} else {
			await archive({
				variables: {
					input: {
						id: archivableItem.id,
					},
				},
			});
		}

		setIsLoading(false);
	};

	return (
		<ActionListItem
			label={t(
				archivableItem.isArchived ? 'actions.list.restore.label' : 'actions.list.archive.label',
				{
					archivableItemName,
				},
			)}
			buttonTitle={t(archivableItem.isArchived ? 'common.restore' : 'common.archive')}
			buttonIndication={
				archivableItem.isArchived ? ButtonIndication.Default : ButtonIndication.Danger
			}
			buttonDisabled={isLoading || buttonDisabled}
			onButtonClick={toggleArchived}
		/>
	);
};

export default ActionListItem;
